import axios from '@/config/backend'
import i18n from '@/i18n/i18n'

function t(key, fallback) {
    const messages = i18n.messages[i18n.locale] || {}
    const parts = key.split('.')
    let cur = messages
    for (const p of parts) {
        if (cur && typeof cur === 'object' && p in cur) cur = cur[p]
        else return fallback
    }
    return typeof cur === 'string' ? cur : fallback
}

// -------- Per-type builders --------
// Each handler receives a normalized envelope and returns the local item shape
// (or null if the payload is malformed). Add a new entry to TYPE_HANDLERS to
// support a new notification type.

function buildCommentItem({ data, apiId, read, createdAt }) {
    const c = data && data.comment
    const a = data && data.author
    if (!c || !c.id) return null
    const fallback = i18n.locale === 'en'
        ? '{name} mentioned you in an internal note'
        : '{name} vous a mentionné dans une note interne'
    const tpl = t('topbar.notifications.event_internal_note', fallback)
    return {
        id: `comment-${c.id}`,
        apiId,
        kind: 'message',
        icon: 'fal fa-comment-alt-lines',
        title: tpl.replace('{name}', (a && a.name) || ''),
        postId: c.post_id || null,
        createdAt: c.created_at || createdAt || new Date().toISOString(),
        link: c.post_id ? `/posts/${c.post_id}` : null,
        read
    }
}

function buildStatusItem({ data, apiId, read, createdAt }) {
    const post = data && data.post
    const status = data && data.status
    const author = data && data.author
    if (!post || !post.id || !status) return null
    const fallback = i18n.locale === 'en'
        ? '{name} changed the status to {status}'
        : '{name} a changé le statut en {status}'
    const tpl = t('topbar.notifications.event_status_changed', fallback)
    return {
        // No domain id for the status transition — fall back to the API uuid.
        id: apiId ? `status-${apiId}` : `status-${post.id}-${Date.now()}`,
        apiId,
        kind: status.final ? 'success' : 'info',
        icon: status.final ? 'fal fa-check-circle' : 'fal fa-flag',
        title: tpl
            .replace('{name}', (author && author.name) || '')
            .replace('{status}', status.name || ''),
        postId: post.id,
        createdAt: createdAt || new Date().toISOString(),
        link: `/posts/${post.id}`,
        read
    }
}

function buildAttributionItem({ data, apiId, read, createdAt }) {
    const post = data && data.post
    const old = data && data.old_operator
    if (!post || !post.id) return null

    let key = 'topbar.notifications.event_post_attributed'
    let fallback = i18n.locale === 'en' ? 'Ticket assigned' : 'Ticket attribué'
    if (data && data.is_post_create) {
        key = 'topbar.notifications.event_post_attributed_new'
        fallback = i18n.locale === 'en' ? 'New ticket to handle' : 'Nouveau ticket à traiter'
    } else if (old && old.name) {
        key = 'topbar.notifications.event_post_attributed_reassigned'
        fallback = i18n.locale === 'en'
            ? 'Ticket reassigned from {name}'
            : 'Ticket réassigné depuis {name}'
    }
    const tpl = t(key, fallback)

    return {
        id: apiId ? `assigned-${apiId}` : `assigned-${post.id}-${Date.now()}`,
        apiId,
        kind: 'info',
        icon: 'fal fa-user-plus',
        title: tpl.replace('{name}', (old && old.name) || ''),
        postId: post.id,
        createdAt: createdAt || new Date().toISOString(),
        link: `/posts/${post.id}`,
        read
    }
}

function buildMessageItem({ data, apiId, read, createdAt }) {
    const post = data && data.post
    const message = data && data.message
    if (!post || !post.id || !message || !message.id) return null

    const fromCitizen = data.sender_type === 'citoyen'
    const key = fromCitizen
        ? 'topbar.notifications.event_message_from_citizen'
        : 'topbar.notifications.event_message_received'
    const fallback = fromCitizen
        ? (i18n.locale === 'en' ? 'New message from the citizen' : 'Nouveau message du citoyen')
        : (i18n.locale === 'en' ? 'New message received' : 'Nouveau message reçu')

    return {
        id: `message-${message.id}`,
        apiId,
        kind: 'info',
        icon: 'fal fa-comments',
        title: t(key, fallback),
        postId: post.id,
        createdAt: createdAt || new Date().toISOString(),
        link: `/posts/${post.id}`,
        read
    }
}

const TYPE_HANDLERS = {
    'App\\Notifications\\PostCommentedNotification': buildCommentItem,
    'App\\Notifications\\PostStatusChanged': buildStatusItem,
    'App\\Notifications\\PostAttributedNotification': buildAttributionItem,
    'App\\Notifications\\PostMessageSentNotification': buildMessageItem
}

function buildFromApi(n) {
    const handler = TYPE_HANDLERS[n.type]
    if (!handler) return null
    return handler({
        data: n.data,
        apiId: n.id,
        read: !!n.read_at,
        createdAt: n.created_at
    })
}

function buildFromBroadcast(notification) {
    const handler = TYPE_HANDLERS[notification.type]
    if (!handler) return null
    // WS payload has data fields at the top level alongside id+type.
    const { id, type, ...data } = notification
    return handler({
        data,
        apiId: id,
        read: false,
        createdAt: null
    })
}

// -------- URL helpers --------

function userScopedUrl(rootState, suffix) {
    const baseURL = rootState.baseURL
    if (!baseURL) return null
    return `${baseURL}/users/me/notifications${suffix}`
}

// -------- Actions --------

export default {
    async fetchAll({ rootState, commit }) {
        const url = userScopedUrl(rootState, '')
        if (!url) return
        try {
            const res = await axios.get(url)
            const apiList = res.data
                && res.data.notifications
                && Array.isArray(res.data.notifications.data)
                ? res.data.notifications.data
                : []
            const list = apiList.map(buildFromApi).filter(Boolean)
            commit('SET_LIST', list)
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('[notifications] fetchAll failed:', e)
        }
    },
    receiveBroadcastNotification({ commit }, notification) {
        if (!notification) return
        const item = buildFromBroadcast(notification)
        if (item) commit('PREPEND', item)
    },
    async markRead({ rootState, state, commit }, id) {
        const item = state.list.find((n) => n.id === id)
        commit('MARK_READ', id)
        if (!item || !item.apiId) return
        const url = userScopedUrl(rootState, `/${item.apiId}/read`)
        if (!url) return
        try {
            await axios.post(url)
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('[notifications] markRead failed:', e)
        }
    },
    async markAllRead({ rootState, commit }) {
        commit('MARK_ALL_READ')
        const url = userScopedUrl(rootState, '/read-all')
        if (!url) return
        try {
            await axios.post(url)
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('[notifications] markAllRead failed:', e)
        }
    },
    clearAll({ commit }) {
        // No DELETE endpoint yet — local-only.
        commit('CLEAR')
    }
}
